import {
  INTRANET_CONTENT_KIND_LABELS,
  INTRANET_SEARCH_MIN_QUERY,
  intranetKnowledgeCategoryPath,
  intranetLibraryPath,
  type IntranetKnowledgeSearchHit,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { usePageTitle } from '@/hooks/use-page-title';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ChannelIcon } from '@/products/intranet/components/intranet-channels';
import { KnowledgeMobileFilters } from '@/products/intranet/components/knowledge-mobile-filters';
import { KnowledgeNarrowing } from '@/products/intranet/components/knowledge-narrowing';
import { KnowledgeSiteFilter } from '@/products/intranet/components/knowledge-site-filter';
import {
  useIntranetKnowledgeCategory,
  useIntranetKnowledgeManufacturersReader,
  useIntranetKnowledgeSearch,
  useIntranetKnowledgeSites,
} from '@/products/intranet/features/intranet/use-intranet-knowledge';
import { INTRANET_LIBRARY_PATH } from '@/products/intranet/intranet-paths';

export function KnowledgeCategoryPage() {
  const { kurzname } = useParams();
  const [params, setParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const knowledgeSites = useIntranetKnowledgeSites();
  const manufacturers = useIntranetKnowledgeManufacturersReader();
  const siteExplicit = params.has('standort');
  const siteId = siteExplicit
    ? (params.get('standort') ?? '')
    : (knowledgeSites.data?.preselectedSiteId ?? '');
  const kind = queryValue(params.get('art'));
  const manufacturerId = queryValue(params.get('hersteller'));
  const libraryKind = kind === 'guide' || kind === 'download' || kind === 'form' ? kind : undefined;
  const category = useIntranetKnowledgeCategory(kurzname, {
    siteId: siteId ? siteId : undefined,
    kind: libraryKind,
    manufacturerId,
  });
  const hits = useIntranetKnowledgeSearch({
    q: activeQuery,
    siteId: siteId ? siteId : undefined,
    categoryId: category.data?.category.id,
    kind: libraryKind,
    manufacturerId,
  });
  const textSearch = activeQuery.length >= INTRANET_SEARCH_MIN_QUERY;

  function writeParam(key: string, value: string) {
    const updated = new URLSearchParams(params);
    if (value) {
      updated.set(key, value);
    } else {
      updated.delete(key);
    }
    setParams(updated, { replace: true });
  }

  function chooseSite(next: string) {
    const updated = new URLSearchParams(params);
    updated.set('standort', next);
    setParams(updated, { replace: true });
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const next = searchText.trim();
    setActiveQuery(next.length >= INTRANET_SEARCH_MIN_QUERY ? next : '');
  }
  const name = category.data?.category.name;
  usePageTitle(name ?? 'Kategorie');

  if (category.isPending) {
    return (
      <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Kategorie wird geladen …
      </p>
    );
  }

  if (category.isError || !category.data) {
    const missing =
      category.error instanceof ApiRequestError && category.error.error.statusCode === 404;
    return (
      <p role="alert" className="py-8 text-sm text-destructive">
        {missing ? 'Diese Kategorie gibt es nicht.' : 'Die Kategorie konnte nicht geladen werden.'}
      </p>
    );
  }

  const { category: topic, pages, subcategories } = category.data;
  const children = subcategories ?? [];

  return (
    <section className="space-y-6">
      <p>
        <Link
          to={{ pathname: INTRANET_LIBRARY_PATH, search: params.toString() }}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Zur Wissensdatenbank
        </Link>
      </p>
      {topic.parent ? (
        <nav aria-label="Pfad" className="flex flex-wrap items-center gap-2 text-sm">
          <Link
            to={{
              pathname: intranetKnowledgeCategoryPath(topic.parent.slug),
              search: params.toString(),
            }}
            className="text-muted-foreground underline-offset-4 hover:underline"
          >
            {topic.parent.name}
          </Link>
          <span aria-hidden="true">/</span>
          <span>{topic.name}</span>
        </nav>
      ) : null}
      {topic.bannerUrl ? (
        <img
          src={`${API_BASE_URL}${topic.bannerUrl}`}
          alt={topic.name}
          className="h-28 w-full rounded-xl object-cover sm:h-36"
        />
      ) : null}
      <PlatformPageHeader
        title={topic.name}
        description={
          topic.parent
            ? 'Seiten, die diese Unterkategorie führt oder zusätzlich nennt.'
            : 'Unterkategorien und die Seiten dieser Hauptkategorie, ihrer Unterkategorien und der weiteren Fundstellen.'
        }
      />
      <form
        role="search"
        onSubmit={submitSearch}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end"
      >
        <label className="flex min-w-0 flex-col gap-1 text-xs sm:col-span-2 sm:text-sm lg:min-w-48 lg:flex-1">
          <span className="text-muted-foreground">Suche</span>
          <Input
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-8 w-full"
          />
        </label>
        <KnowledgeMobileFilters>
          <KnowledgeSiteFilter
            sites={knowledgeSites.data?.sites ?? []}
            siteId={siteId}
            onChange={chooseSite}
          />
          <KnowledgeNarrowing
            kind={kind}
            onKind={(value) => writeParam('art', value)}
            manufacturerId={manufacturerId}
            manufacturers={manufacturers.data?.items ?? []}
            onManufacturer={(value) => writeParam('hersteller', value)}
          />
        </KnowledgeMobileFilters>
      </form>
      {topic.parent ? null : (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <ChannelIcon name={topic.iconName} />
          <span>Kurzname {topic.slug}</span>
        </p>
      )}
      {children.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {children.map((child) => (
            <li key={child.id}>
              <Link
                to={{
                  pathname: intranetKnowledgeCategoryPath(child.slug),
                  search: params.toString(),
                }}
                className="flex min-h-11 items-center gap-2 rounded-xl border border-border bg-muted/40 px-4 py-3 font-semibold text-primary hover:border-primary focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
              >
                <Badge variant="secondary">Kategorie</Badge>
                {child.name}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {textSearch ? (
        <SearchHits pending={hits.isPending} error={hits.isError} items={hits.data?.items ?? []} />
      ) : pages.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card px-4 py-12 text-center text-sm">
          In dieser Kategorie liegen noch keine veröffentlichten Seiten.
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {pages.map((page) => (
            <li key={page.id}>
              <Link
                to={intranetLibraryPath(page.slug)}
                className="flex h-full min-h-11 flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-sm hover:border-primary focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
              >
                <span className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Seite</Badge>
                  <Badge variant="outline">{INTRANET_CONTENT_KIND_LABELS[page.kind]}</Badge>
                </span>
                <span className="font-semibold text-primary">{page.title}</span>
                {page.teaser ? (
                  <span className="line-clamp-2 text-sm text-muted-foreground">{page.teaser}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function queryValue(value: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  return value;
}

function SearchHits({
  pending,
  error,
  items,
}: {
  pending: boolean;
  error: boolean;
  items: readonly IntranetKnowledgeSearchHit[];
}) {
  if (pending) {
    return <p className="text-sm text-muted-foreground">Suche läuft …</p>;
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Die Suche ist fehlgeschlagen.
      </p>
    );
  }
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Treffer.</p>;
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={intranetLibraryPath(item.slug)}
            className="flex h-full min-h-11 flex-col gap-1 rounded-xl border border-border bg-card p-4"
          >
            <span className="font-semibold text-primary">{item.title}</span>
            {item.teaser ? (
              <span className="line-clamp-2 text-sm text-muted-foreground">{item.teaser}</span>
            ) : null}
            {item.leadingCategory ? (
              <span className="text-sm text-muted-foreground">{item.leadingCategory.name}</span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}
